import { jsPDF } from 'jspdf';
import { isNonWorkingDay } from './italianHolidays';

const DAY_NAMES = ['Dom', 'Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab'];
const MONTH_NAMES = [
  'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

export function generateTimesheetPdf(timesheet, client, user) {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = 20;

  // Header
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  const profName = `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Professionista';
  doc.text(profName, margin, y);

  y += 8;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  doc.text('Timesheet', margin, y);

  // Mese/anno a destra
  const monthYear = `${MONTH_NAMES[timesheet.month - 1]} ${timesheet.year}`;
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.text(monthYear, pageWidth - margin, 20, { align: 'right' });

  // Cliente
  y += 12;
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('Cliente:', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(client?.name || timesheet.clientName || '-', margin + 20, y);

  if (client?.vatNumber) {
    y += 5;
    doc.text(`P.IVA: ${client.vatNumber}`, margin, y);
  }
  if (client?.fiscalCode) {
    y += 5;
    doc.text(`C.F.: ${client.fiscalCode}`, margin, y);
  }

  // Tabella presenze
  y += 12;
  const colWidths = [15, 20, 25, pageWidth - margin * 2 - 60];
  const headers = ['Giorno', 'Nome', 'Presenza', 'Attività'];

  // Header tabella
  doc.setFillColor(240, 240, 240);
  doc.rect(margin, y - 4, pageWidth - margin * 2, 7, 'F');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  let x = margin;
  headers.forEach((h, i) => {
    doc.text(h, x + 2, y);
    x += colWidths[i];
  });

  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);

  const daysInMonth = new Date(timesheet.year, timesheet.month, 0).getDate();
  const entries = timesheet.entries || [];

  for (let d = 1; d <= daysInMonth; d++) {
    if (y > 270) {
      doc.addPage();
      y = 20;
    }

    const entry = entries.find((e) => e.day === d);
    const nonWorking = isNonWorkingDay(d, timesheet.month, timesheet.year);
    const date = new Date(timesheet.year, timesheet.month - 1, d);
    const dayName = DAY_NAMES[date.getDay()];

    // Background per weekend/festivi
    if (nonWorking) {
      doc.setFillColor(245, 245, 245);
      doc.rect(margin, y - 3.5, pageWidth - margin * 2, 5, 'F');
      doc.setTextColor(150);
    } else {
      doc.setTextColor(0);
    }

    x = margin;
    doc.text(String(d), x + 2, y);
    x += colWidths[0];
    doc.text(dayName, x + 2, y);
    x += colWidths[1];

    const val = entry?.value;
    const presText = val === 1 ? '1' : val === 0.5 ? '0.5' : '';
    doc.text(presText, x + 2, y);
    x += colWidths[2];

    doc.text((entry?.task || '').substring(0, 60), x + 2, y);

    y += 5;
  }

  // Riepilogo
  y += 8;
  if (y > 260) {
    doc.addPage();
    y = 20;
  }

  doc.setDrawColor(200);
  doc.line(margin, y, pageWidth - margin, y);
  y += 8;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0);
  doc.text('Riepilogo', margin, y);

  y += 7;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');

  const totalDays = timesheet.totalDays || 0;
  const rate = timesheet.rate || 0;
  const totalAmount = timesheet.totalAmount || 0;
  const compLabel = timesheet.compensationType === 'daily' ? '/gg' : timesheet.compensationType === 'hourly' ? '/h' : '';

  doc.text(`Giorni lavorati: ${totalDays}`, margin, y);
  y += 5;
  if (rate > 0) {
    doc.text(`Tariffa: ${rate.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €${compLabel}`, margin, y);
    y += 5;
  }
  doc.setFont('helvetica', 'bold');
  doc.text(`Totale: ${totalAmount.toLocaleString('it-IT', { minimumFractionDigits: 2 })} €`, margin, y);

  // Spazi firma
  y += 20;
  if (y > 255) {
    doc.addPage();
    y = 40;
  }

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.setTextColor(100);

  const signWidth = 60;
  const leftX = margin;
  const rightX = pageWidth - margin - signWidth;

  doc.text('Il Professionista', leftX, y);
  doc.text('Il Cliente', rightX, y);
  y += 3;
  doc.line(leftX, y, leftX + signWidth, y);
  doc.line(rightX, y, rightX + signWidth, y);

  // Footer
  const footerY = doc.internal.pageSize.getHeight() - 10;
  doc.setFontSize(7);
  doc.setTextColor(180);
  doc.text('Generato con Forfy', pageWidth / 2, footerY, { align: 'center' });

  // Download
  const filename = `Timesheet_${timesheet.clientName || 'cliente'}_${MONTH_NAMES[timesheet.month - 1]}_${timesheet.year}.pdf`;
  doc.save(filename.replace(/\s+/g, '_'));
}
